import React, { useState, useEffect } from 'react';
import { Button, Grid, Card, CardContent, Box } from '@mui/material';
import FilterSelectInput from '../../../sharedModules/form/components/FilterSelectInput';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import axios from '../../../sharedModules/utils/axios.utils';

interface ProjectCommunicationsFilterProps {
  filterValues: Record<string, string>;
  setFilterValues: React.Dispatch<any>;
  onFilter: (filterValues: any) => void;
}

const ProjectCommunicationsFilter: React.FC<ProjectCommunicationsFilterProps> = (props) => {
  const { filterValues, setFilterValues, onFilter } = props;

  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [salespersonOptions, setSalespersonOptions] = useState<string[]>([]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get<string, any>('/customer_issues/servman_project_communications/filter_options');
        if (response?.data) {
          setStatusOptions(response.data.status_options || []);
          setDepartmentOptions(response.data.department_options || []);
          setSalespersonOptions(response.data.salesperson_options || []);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchFilterOptions();
  }, []);

  const handleFiltersKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') onFilter(filterValues);
  };

  const handleFilterChange = (key: string, value: string) => {
    const updatedFilterValues = { ...filterValues, [key]: value };
    setFilterValues(updatedFilterValues);

    if (['status_eq', 'db_location_eq', 'department_eq'].includes(key)) {
      onFilter(updatedFilterValues);
    }
  };

  const handleClear = () => {
    const clearedFilters = {
      status_eq: 'all',
      db_location_eq: 'all',
      department_eq: 'all',
    };
    setFilterValues(clearedFilters);
    onFilter(clearedFilters);
  };

  const handleApply = () => {
    onFilter(filterValues);
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center" onKeyDown={handleFiltersKeyPress}>
          <Grid item xs={12} sm={6} md={2.4}>
            <FilterSelectInput
              value={filterValues?.status_eq || 'all'}
              choices={[
                { label: 'All Statuses', value: 'all' },
                ...statusOptions.map((status) => ({ label: status, value: status })),
              ]}
              valueChanged={(value) => handleFilterChange('status_eq', value)}
              label="Status"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FilterSelectInput
              value={filterValues?.department_eq || 'all'}
              choices={[
                { label: 'All Departments', value: 'all' },
                ...departmentOptions.map((dept) => ({ label: dept, value: dept })),
              ]}
              valueChanged={(value) => handleFilterChange('department_eq', value)}
              label="Department"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FilterSelectInput
              value={filterValues?.db_location_eq || 'all'}
              choices={[
                { label: 'All Locations', value: 'all' },
                { label: 'Pittsburgh', value: 'pittsburgh' },
                { label: 'Tampa', value: 'tampa' },
              ]}
              valueChanged={(value) => handleFilterChange('db_location_eq', value)}
              label="Location"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'flex-end', pb: 0.5 }}>
              <Button variant="outlined" startIcon={<FilterListIcon />} onClick={handleApply} sx={{ flex: 1 }}>
                Apply
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ flex: 1 }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ProjectCommunicationsFilter;
